import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Title } from 'react-admin';
import { getErrorMessage } from 'shared/utils/get-error-message';
import { formatStatus, formatTableDateTime, isCourseArchived } from './lib/formatters';
import { buildReorderItems } from './lib/reorder';
import {
  actionButtonColumnSx,
  actionColumnStackSx,
  actionReorderRowSx,
  contentCardSx,
  contentTableContainerSx,
  contentTableSx,
  folderTableCellSx,
  reorderButtonSx,
  videoTableCellSx,
} from './lib/table-styles';
import {
  ContentCourseDetail,
  ContentFolderItem,
  ContentVideoItem,
  DEFAULT_FOLDER_FORM,
  DEFAULT_VIDEO_FORM,
  deleteFolder,
  deleteVideo,
  FolderForm,
  loadCourseById,
  reorderFolders,
  reorderVideos,
  restoreCourse,
  saveFolder,
  saveVideo,
  uploadContentFile,
  VideoForm,
} from './model';
import { ConfirmDeleteDialog, FolderFormDialog } from './ui/folder-form-dialog';
import { CopyIdButton } from './ui/copy-id-button';
import { VideoFormDialog } from './ui/video-form-dialog';

type Props = {
  courseId: string;
  onBack: () => void;
};

export const ContentCourseDetailView = ({ courseId, onBack }: Props) => {
  const [course, setCourse] = useState<ContentCourseDetail | null>(null);
  const [folders, setFolders] = useState<ContentFolderItem[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [folderForm, setFolderForm] = useState<FolderForm>(DEFAULT_FOLDER_FORM(courseId));
  const [folderFormError, setFolderFormError] = useState<string | null>(null);
  const [isFolderSaving, setIsFolderSaving] = useState(false);

  const [deleteFolderId, setDeleteFolderId] = useState<string | null>(null);
  const [deleteFolderError, setDeleteFolderError] = useState<string | null>(null);
  const [isFolderDeleting, setIsFolderDeleting] = useState(false);

  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [videoForm, setVideoForm] = useState<VideoForm | null>(null);
  const [videoFormError, setVideoFormError] = useState<string | null>(null);
  const [isVideoSaving, setIsVideoSaving] = useState(false);

  const [deleteVideoId, setDeleteVideoId] = useState<string | null>(null);
  const [deleteVideoError, setDeleteVideoError] = useState<string | null>(null);
  const [isVideoDeleting, setIsVideoDeleting] = useState(false);
  const [isCourseRestoring, setIsCourseRestoring] = useState(false);

  const selectedFolder = folders.find((folder) => folder.id === selectedFolderId) ?? null;
  const selectedVideos = useMemo(
    () =>
      [...(selectedFolder?.videos ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [selectedFolder],
  );
  const isArchived = course ? isCourseArchived(course) : false;

  const refreshCourse = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await loadCourseById(courseId);
      setCourse(response);
      const nextFolders = response.folders || [];
      setFolders(nextFolders);

      setSelectedFolderId((currentId) => {
        if (currentId && nextFolders.some((folder) => folder.id === currentId)) {
          return currentId;
        }

        return nextFolders[0]?.id ?? null;
      });
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка загрузки курса'));
    } finally {
      setIsLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    refreshCourse();
  }, [refreshCourse]);

  const openCreateFolder = () => {
    setFolderForm(DEFAULT_FOLDER_FORM(courseId));
    setFolderFormError(null);
    setIsFolderDialogOpen(true);
  };

  const openEditFolder = (folder: ContentFolderItem) => {
    setFolderForm({
      id: folder.id,
      courseId: folder.courseId,
      name: folder.name,
      isSequentialLock: folder.isSequentialLock,
      status: folder.status,
    });
    setFolderFormError(null);
    setIsFolderDialogOpen(true);
  };

  const validateFolder = () => {
    if (!folderForm.name.trim()) return 'Укажите название папки';
    return null;
  };

  const handleSaveFolder = async () => {
    const validationError = validateFolder();
    if (validationError) {
      setFolderFormError(validationError);
      return;
    }

    setIsFolderSaving(true);
    setFolderFormError(null);

    const payload: Record<string, unknown> = {
      courseId: folderForm.courseId,
      name: folderForm.name.trim(),
      isSequentialLock: folderForm.isSequentialLock,
      status: folderForm.status,
    };

    if (folderForm.id) payload.id = folderForm.id;

    try {
      await saveFolder(payload);
      setIsFolderDialogOpen(false);
      await refreshCourse();
    } catch (e: unknown) {
      setFolderFormError(getErrorMessage(e, 'Ошибка сохранения папки'));
    } finally {
      setIsFolderSaving(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!deleteFolderId) return;

    setIsFolderDeleting(true);
    setDeleteFolderError(null);

    try {
      await deleteFolder(deleteFolderId);
      setDeleteFolderId(null);
      await refreshCourse();
    } catch (e: unknown) {
      setDeleteFolderError(getErrorMessage(e, 'Ошибка удаления папки'));
    } finally {
      setIsFolderDeleting(false);
    }
  };

  const handleReorderFolder = async (folderId: string, direction: 'up' | 'down') => {
    const items = buildReorderItems(folders, folderId, direction);
    if (!items) return;

    try {
      await reorderFolders(items);
      await refreshCourse();
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка изменения порядка папок'));
    }
  };

  const openCreateVideo = () => {
    if (!selectedFolder) return;

    setVideoForm(DEFAULT_VIDEO_FORM(selectedFolder.id));
    setVideoFormError(null);
    setIsVideoDialogOpen(true);
  };

  const openEditVideo = (video: ContentVideoItem) => {
    setVideoForm({
      id: video.id,
      folderId: video.folderId,
      src: video.src,
      name: video.name,
      description: video.description || '',
      status: video.status,
      files: (video.files || []).map((file) => ({ id: file.url, ...file })),
      needUserFeedback: Boolean(video.needUserFeedback),
      feedbackRequiredToProgress: Boolean(video.feedbackRequiredToProgress),
    });
    setVideoFormError(null);
    setIsVideoDialogOpen(true);
  };

  const validateVideo = () => {
    if (!videoForm?.src.trim()) return 'Поле src обязательно';
    if (!videoForm?.name.trim()) return 'Укажите название видео';
    if (videoForm.files.length > 10) return 'Можно загрузить не больше 10 файлов';
    if (videoForm.files.some((file) => !file.name.trim()))
      return 'Укажите название для каждого файла';
    if (videoForm.files.some((file) => !file.url && !file.file))
      return 'Файл не выбран';
    return null;
  };

  const handleSaveVideo = async () => {
    if (!videoForm) return;

    const validationError = validateVideo();
    if (validationError) {
      setVideoFormError(validationError);
      return;
    }

    setIsVideoSaving(true);
    setVideoFormError(null);

    try {
      const files = await Promise.all(
        videoForm.files.map(async (file) => ({
          name: file.name.trim(),
          url: file.url || (await uploadContentFile(file.file!)),
        })),
      );
      const payload: Record<string, unknown> = {
        folderId: videoForm.folderId,
        src: videoForm.src.trim(),
        name: videoForm.name.trim(),
        status: videoForm.status,
        description: videoForm.description.trim() || null,
        files,
        needUserFeedback: videoForm.needUserFeedback,
        feedbackRequiredToProgress: videoForm.needUserFeedback
          ? videoForm.feedbackRequiredToProgress
          : false,
      };

      if (videoForm.id) payload.id = videoForm.id;

      await saveVideo(payload);
      setIsVideoDialogOpen(false);
      setVideoForm(null);
      await refreshCourse();
    } catch (e: unknown) {
      setVideoFormError(getErrorMessage(e, 'Ошибка сохранения видео'));
    } finally {
      setIsVideoSaving(false);
    }
  };

  const handleDeleteVideo = async () => {
    if (!deleteVideoId) return;

    setIsVideoDeleting(true);
    setDeleteVideoError(null);

    try {
      await deleteVideo(deleteVideoId);
      setDeleteVideoId(null);
      await refreshCourse();
    } catch (e: unknown) {
      setDeleteVideoError(getErrorMessage(e, 'Ошибка удаления видео'));
    } finally {
      setIsVideoDeleting(false);
    }
  };

  const handleReorderVideo = async (
    folderId: string,
    videos: ContentVideoItem[],
    videoId: string,
    direction: 'up' | 'down',
  ) => {
    const items = buildReorderItems(videos, videoId, direction);
    if (!items) return;

    try {
      await reorderVideos(folderId, items);
      await refreshCourse();
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка изменения порядка видео'));
    }
  };

  const handleRestoreCourse = async () => {
    if (!course) return;

    setIsCourseRestoring(true);
    setError(null);

    try {
      await restoreCourse(course.id);
      await refreshCourse();
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка восстановления курса'));
    } finally {
      setIsCourseRestoring(false);
    }
  };

  const deleteFolderTarget = folders.find((folder) => folder.id === deleteFolderId);

  return (
    <Box sx={{ p: 2 }}>
      <Title title={course ? `Курс: ${course.name}` : 'Курс'} />

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={onBack}>
          ← К списку курсов
        </Button>
        <Button variant="outlined" onClick={refreshCourse} disabled={isLoading}>
          Обновить
        </Button>
      </Stack>

      {course?.description && (
        <Typography sx={{ mb: 2 }} color="text.secondary">
          {course.description}
        </Typography>
      )}

      {isArchived && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={isCourseRestoring}
              onClick={handleRestoreCourse}
            >
              Восстановить
            </Button>
          }
        >
          Курс в архиве и скрыт в B2B ЛК. Папки и видео ниже доступны только для просмотра.
        </Alert>
      )}

      <Card sx={{ mb: 2, ...contentCardSx }}>
        <CardHeader
          title="Папки"
          action={
            <Button variant="contained" onClick={openCreateFolder} disabled={isArchived}>
              Создать папку
            </Button>
          }
        />

        {isLoading ? <LinearProgress /> : <Box sx={{ height: 4 }} />}

        <CardContent>
          {error && (
            <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {folders.length === 0 && !isLoading ? (
            <Typography>Папок пока нет</Typography>
          ) : (
            <TableContainer sx={contentTableContainerSx}>
              <Table size="small" sx={contentTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={folderTableCellSx.index}>#</TableCell>
                    <TableCell sx={folderTableCellSx.name}>Название</TableCell>
                    <TableCell sx={folderTableCellSx.status}>Статус</TableCell>
                    <TableCell sx={folderTableCellSx.lock}>
                      <Tooltip title="Если включено, следующая папка откроется только после просмотра всех видео в этой">
                        <Typography component="span" sx={{ cursor: 'help', borderBottom: '1px dotted' }}>
                          Блокировка
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={folderTableCellSx.count}>Видео</TableCell>
                    <TableCell sx={folderTableCellSx.date}>Создана</TableCell>
                    <TableCell sx={folderTableCellSx.date}>Изменена</TableCell>
                    <TableCell sx={folderTableCellSx.id}>ID</TableCell>
                    <TableCell sx={folderTableCellSx.actions}>
                      Действия
                    </TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {folders.map((folder, index) => {
                  const isSelected = folder.id === selectedFolderId;

                  return (
                    <TableRow
                      key={folder.id}
                      hover
                      selected={isSelected}
                      onClick={() => setSelectedFolderId(folder.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={folderTableCellSx.index}>{index + 1}</TableCell>
                      <TableCell sx={folderTableCellSx.name}>
                        <Typography component="span">{folder.name}</Typography>
                      </TableCell>
                      <TableCell sx={folderTableCellSx.status}>
                        <Chip
                          size="small"
                          label={formatStatus(folder.status)}
                          color={folder.status === 'published' ? 'success' : 'default'}
                          variant={folder.status === 'published' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell sx={folderTableCellSx.lock}>
                        {folder.isSequentialLock ? (
                          <Tooltip title="Следующая папка заблокирована, пока не просмотрены все видео здесь">
                            <Chip size="small" color="warning" label="Да" />
                          </Tooltip>
                        ) : (
                          'Нет'
                        )}
                      </TableCell>
                      <TableCell sx={folderTableCellSx.count}>{folder.videos?.length || 0}</TableCell>
                      <TableCell sx={folderTableCellSx.date}>
                        {formatTableDateTime(folder.createdAt)}
                      </TableCell>
                      <TableCell sx={folderTableCellSx.date}>
                        {formatTableDateTime(folder.updatedAt)}
                      </TableCell>
                      <TableCell sx={folderTableCellSx.id}>
                        <CopyIdButton id={folder.id} />
                      </TableCell>
                      <TableCell sx={folderTableCellSx.actions}>
                        {isArchived ? (
                          '—'
                        ) : (
                          <Stack direction="column" {...actionColumnStackSx}>
                            <Stack {...actionReorderRowSx}>
                              <Button
                                size="small"
                                sx={reorderButtonSx}
                                disabled={index === 0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReorderFolder(folder.id, 'up');
                                }}
                              >
                                ↑
                              </Button>
                              <Button
                                size="small"
                                sx={reorderButtonSx}
                                disabled={index === folders.length - 1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReorderFolder(folder.id, 'down');
                                }}
                              >
                                ↓
                              </Button>
                            </Stack>
                            <Button
                              size="small"
                              sx={actionButtonColumnSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditFolder(folder);
                              }}
                            >
                              Изменить
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              sx={actionButtonColumnSx}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteFolderId(folder.id);
                                setDeleteFolderError(null);
                              }}
                            >
                              Удалить
                            </Button>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {selectedFolder && (
        <Card sx={contentCardSx}>
          <CardHeader
            title={`Видео в папке: ${selectedFolder.name}`}
            action={
              <Button variant="contained" onClick={openCreateVideo} disabled={isArchived}>
                Добавить видео
              </Button>
            }
          />
          <CardContent>
            {selectedVideos.length === 0 ? (
              <Typography>В этой папке пока нет видео</Typography>
            ) : (
              <TableContainer sx={contentTableContainerSx}>
                <Table size="small" sx={contentTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={videoTableCellSx.index}>#</TableCell>
                      <TableCell sx={videoTableCellSx.name}>Название</TableCell>
                      <TableCell sx={videoTableCellSx.description}>Описание</TableCell>
                      <TableCell>Файлы</TableCell>
                      <TableCell sx={videoTableCellSx.status}>Статус</TableCell>
                      <TableCell sx={videoTableCellSx.link}>Ссылка</TableCell>
                      <TableCell sx={videoTableCellSx.date}>Создано</TableCell>
                      <TableCell sx={videoTableCellSx.date}>Изменено</TableCell>
                      <TableCell sx={videoTableCellSx.id}>ID</TableCell>
                      <TableCell sx={videoTableCellSx.actions}>
                        Действия
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedVideos.map((video, index) => (
                      <TableRow key={video.id} hover>
                        <TableCell sx={videoTableCellSx.index}>{index + 1}</TableCell>
                        <TableCell sx={videoTableCellSx.name}>
                          <Typography component="span">{video.name}</Typography>
                        </TableCell>
                        <TableCell sx={videoTableCellSx.description}>
                          {video.description ? (
                            <Typography component="span">{video.description}</Typography>
                          ) : (
                            <Typography component="span" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {(video.files || []).map((file) => (
                              <Button
                                key={file.url}
                                component="a"
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                size="small"
                                sx={{ justifyContent: 'flex-start' }}
                              >
                                {file.name}
                              </Button>
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell sx={videoTableCellSx.status}>
                          <Chip
                            size="small"
                            label={formatStatus(video.status)}
                            color={video.status === 'published' ? 'success' : 'default'}
                            variant={video.status === 'published' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell sx={videoTableCellSx.link}>
                          <Typography
                            component="a"
                            href={video.src}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ color: 'primary.main' }}
                          >
                            {video.src}
                          </Typography>
                        </TableCell>
                        <TableCell sx={videoTableCellSx.date}>
                          {formatTableDateTime(video.createdAt)}
                        </TableCell>
                        <TableCell sx={videoTableCellSx.date}>
                          {formatTableDateTime(video.updatedAt)}
                        </TableCell>
                        <TableCell sx={videoTableCellSx.id}>
                          <CopyIdButton id={video.id} />
                        </TableCell>
                        <TableCell sx={videoTableCellSx.actions}>
                          {isArchived ? (
                            '—'
                          ) : (
                            <Stack direction="column" {...actionColumnStackSx}>
                              <Stack {...actionReorderRowSx}>
                                <Button
                                  size="small"
                                  sx={reorderButtonSx}
                                  disabled={index === 0}
                                  onClick={() =>
                                    handleReorderVideo(
                                      selectedFolder.id,
                                      selectedVideos,
                                      video.id,
                                      'up',
                                    )
                                  }
                                >
                                  ↑
                                </Button>
                                <Button
                                  size="small"
                                  sx={reorderButtonSx}
                                  disabled={index === selectedVideos.length - 1}
                                  onClick={() =>
                                    handleReorderVideo(
                                      selectedFolder.id,
                                      selectedVideos,
                                      video.id,
                                      'down',
                                    )
                                  }
                                >
                                  ↓
                                </Button>
                              </Stack>
                              <Button
                                size="small"
                                sx={actionButtonColumnSx}
                                onClick={() => openEditVideo(video)}
                              >
                                Изменить
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                sx={actionButtonColumnSx}
                                onClick={() => {
                                  setDeleteVideoId(video.id);
                                  setDeleteVideoError(null);
                                }}
                              >
                                Удалить
                              </Button>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <FolderFormDialog
        open={isFolderDialogOpen}
        form={folderForm}
        isSaving={isFolderSaving}
        error={folderFormError}
        onClose={() => !isFolderSaving && setIsFolderDialogOpen(false)}
        onSave={handleSaveFolder}
        onChange={setFolderForm}
      />

      {videoForm && (
        <VideoFormDialog
          open={isVideoDialogOpen}
          form={videoForm}
          isSaving={isVideoSaving}
          error={videoFormError}
          onError={setVideoFormError}
          onClose={() => !isVideoSaving && setIsVideoDialogOpen(false)}
          onSave={handleSaveVideo}
          onChange={setVideoForm}
        />
      )}

      <ConfirmDeleteDialog
        open={Boolean(deleteFolderId)}
        title="Удалить папку?"
        description={
          deleteFolderTarget
            ? `Папка «${deleteFolderTarget.name}» и все видео внутри (${deleteFolderTarget.videos.length}) будут удалены.`
            : 'Папка будет удалена.'
        }
        isDeleting={isFolderDeleting}
        error={deleteFolderError}
        onClose={() => !isFolderDeleting && setDeleteFolderId(null)}
        onConfirm={handleDeleteFolder}
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteVideoId)}
        title="Удалить видео?"
        description="Действие необратимо. Видео будет удалено."
        isDeleting={isVideoDeleting}
        error={deleteVideoError}
        onClose={() => !isVideoDeleting && setDeleteVideoId(null)}
        onConfirm={handleDeleteVideo}
      />
    </Box>
  );
};

export { ContentCourseDetailView as ContentCourseDetail };
